import { useState, useCallback, useRef, useEffect } from 'react';
import i18n from '@/i18n';
import { useAuth } from '@/contexts/AuthContext';
import { useTenantSelection } from '@/contexts/TenantSelectionContext';
import { useTenantConversationMessages } from './useTenantConversationMessages';
import type { Message } from '@/types/message-types';
import { MessageDataService } from '@/services/MessageDataService';
import { whatsappMediaService } from '@/services/whatsappMediaService';
import { FEATURES } from '@/config/features';

// Cache messages by conversation ID to avoid refetch on tab switch
// Map structure: conversationId -> { messages, timestamp }
const messageCache = new Map<string, {
  messages: Message[];
  timestamp: number;
}>();

export function useMessageData(
  conversationId: string,
  overrideTenantId?: string,
  customerId?: string,
  allConversationIds?: string[]
) {
  const { userProfile } = useAuth();
  const { selectedTenant } = useTenantSelection();
  const tenantId = overrideTenantId || (userProfile as Record<string, unknown>)?.tenant_id as string || '';
  const tenantSchema = selectedTenant?.schema_name;

  // Use the enhanced hook with infinite scroll support
  const {
    messages: originalMessages,
    loading: isLoading,
    error: rawError,
    refetch: loadMessages,
    loadMoreMessages,
    hasMoreMessages,
    isLoadingMore
  } = useTenantConversationMessages(tenantId, conversationId, tenantSchema, customerId);

  const [messages, setMessages] = useState<Message[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [localLoading, setLocalLoading] = useState(true);  // [FIX: CACHE-LOADING] Local loading state we control
  const instanceIdRef = useRef(`msg-data-${Math.random().toString(36).substring(7)}`);
  const messageHashRef = useRef<string>('');
  const hmrRecoveryRef = useRef(false);
  const usingCacheRef = useRef(false);

  // Refs for customer mode — used in addMessage closure without stale captures
  const customerIdRef = useRef(customerId);
  const allConversationIdsRef = useRef(allConversationIds);
  // Cache key: customer-based in customer mode, conversation-based otherwise
  const cacheKey = customerId ? `customer-${customerId}` : conversationId;

  // Keep refs in sync with props
  useEffect(() => { customerIdRef.current = customerId; }, [customerId]);
  useEffect(() => { allConversationIdsRef.current = allConversationIds; }, [allConversationIds]);

  // Update local messages when originalMessages change - with detailed logging
  useEffect(() => {
    // [FIX: CACHE-RACE] Skip if using cached data (Phase 4 optimization)
    if (usingCacheRef.current) {
      console.log('⏭️ Skipping originalMessages update - using cache');
      usingCacheRef.current = false;  // Reset for next time
      return;
    }

    console.log('🔄 useMessageData: originalMessages changed', {
      originalCount: originalMessages.length,
      conversationId,
      hasAIMessages: originalMessages.filter(m => m.sender_type === 'ai_agent_automatic').length,
      latestMessages: originalMessages.slice(-3).map(m => ({
        id: m.id,
        sender_type: m.sender_type,
        content: m.content?.substring(0, 30)
      }))
      // Removed currentCount - caused extra useEffect re-runs
    });

    setMessages(originalMessages);

    // [FIX: MESSAGE-MIXING] Filter AbortError from UI display
    const filteredError = rawError && rawError.includes('AbortError') ? null : rawError;
    setError(filteredError);

    // Update cache after successful fetch - ONLY if feature enabled
    if (cacheKey && originalMessages.length > 0 && FEATURES.ENABLE_MESSAGE_CACHE) {
      messageCache.set(cacheKey, {
        messages: originalMessages,
        timestamp: Date.now()
      });
      console.log('💾 Cached messages', {
        cacheKey,
        count: originalMessages.length
      });
    }

    console.log('📊 useMessageData: State updated', {
      newMessageCount: originalMessages.length,
      aiMessageCount: originalMessages.filter(m => m.sender_type === 'ai_agent_automatic').length
    });
  }, [originalMessages, rawError]);

  // [FIX: MESSAGE-MIXING] Clear error state immediately when conversation/customer changes
  useEffect(() => {
    usingCacheRef.current = false;  // [FIX: CACHE-RACE] Reset cache flag
    setError(null);
    // [FIX: CACHE-LOADING] Conditional loading reset — check cache BEFORE setting loading
    // Cache hit = 0 spinner frames. Cache miss = show skeleton until data arrives.
    const currentCacheKey = customerIdRef.current ? `customer-${customerIdRef.current}` : conversationId;
    const hasCachedData = FEATURES.ENABLE_MESSAGE_CACHE && messageCache.has(currentCacheKey);
    if (!hasCachedData) {
      setLocalLoading(true);
    }
  }, [conversationId, customerId]);

  // [FIX: CACHE-LOADING] Sync local loading with underlying loading when database fetch completes
  useEffect(() => {
    if (!usingCacheRef.current) {
      // Only sync if we're not using cache (database fetch in progress)
      setLocalLoading(isLoading);
    }
  }, [isLoading]);

  // HMR recovery mechanism for development
  useEffect(() => {
    if (import.meta.env.DEV && messages.length > 0) {
      const imageMessages = messages.filter(m => m.message_type === 'image');
      if (imageMessages.length > 0 && !hmrRecoveryRef.current) {
        whatsappMediaService.clearCache();
        hmrRecoveryRef.current = true;
      }
    }
  }, [messages]);

  // Silent refresh for background updates without loading indicators
  const silentRefresh = useCallback(async () => {
    if (!conversationId && !customerId) return;

    console.log('🔄 useMessageData: silentRefresh called', {
      conversationId
      // Removed message counts - caused callback instability
    });

    try {
      await loadMessages();
      console.log('✅ useMessageData: silentRefresh completed successfully');
    } catch (fetchError) {
      // [FIX: MESSAGE-MIXING] Ignore abort errors (expected when switching conversations)
      if (fetchError instanceof Error && fetchError.name === 'AbortError') {
        console.log('✅ [FIX: MESSAGE-MIXING] Silent refresh aborted (conversation switched)');
        return;
      }

      console.error(`❌ useMessageData: Silent refresh error:`, fetchError);
      setError(fetchError instanceof Error ? fetchError.message : i18n.t('conversations:hookErrors.refreshFailed'));
    }
  }, [conversationId, loadMessages]);

  // Wrapper for the loadMessages function - just use the hook's refetch
  const loadMessagesWrapper = useCallback(async (forceReload: boolean = false) => {
    if (!conversationId && !customerId) return;

    console.log('🔄 useMessageData: loadMessagesWrapper called', {
      cacheKey,
      forceReload,
      cacheEnabled: FEATURES.ENABLE_MESSAGE_CACHE
    });

    // Check cache first (unless force reload) - ONLY if feature enabled
    if (!forceReload && FEATURES.ENABLE_MESSAGE_CACHE) {
      const cached = messageCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < FEATURES.MESSAGE_CACHE_TTL) {
        console.log('✅ Using cached messages', {
          cacheKey,
          cacheAge: Date.now() - cached.timestamp,
          messageCount: cached.messages.length
        });
        usingCacheRef.current = true;
        setMessages(cached.messages);  // Update local state directly
        setLocalLoading(false);  // [FIX: CACHE-LOADING] Clear loading state when using cache
        return;  // Skip database fetch
      }
    }

    // Cache miss or force reload - fetch from database
    setLocalLoading(true);  // [FIX: CACHE-LOADING] Set loading before database fetch
    await loadMessages();
  }, [cacheKey, conversationId, customerId, loadMessages]);

  const addMessage = useCallback((message: Message) => {
    console.log('📨 useMessageData: addMessage called', {
      messageId: message.id,
      senderType: message.sender_type,
      content: message.content?.substring(0, 50),
      isAIMessage: message.sender_type === 'ai_agent_automatic',
      timestamp: message.created_at
    });
    
    setMessages(prev => {
      // Enhanced deduplication for all message types
      const existsById = prev.some(m => m.id === message.id);
      const existsByWhatsAppId = message.external_id && prev.some(m => m.external_id === message.external_id);
      
      // Check for database message ID match (for optimistic messages)
      const databaseMessageId = message.metadata?.database_message_id;
      const existsByDatabaseId = databaseMessageId && prev.some(m => m.id === databaseMessageId);
      
      // Enhanced content-based deduplication for AI messages
      const isAIMessage = message.sender_type === 'ai_agent_automatic';
      const existsByContentAndTime = isAIMessage && prev.some(m => 
        m.sender_type === 'ai_agent_automatic' &&
        m.content === message.content &&
        Math.abs(new Date(m.created_at).getTime() - new Date(message.created_at).getTime()) < 5000 // 5 seconds
      );
      
      if (existsById || existsByWhatsAppId || existsByDatabaseId || existsByContentAndTime) {
        console.log('🔄 MessageData: Duplicate message detected, skipping', {
          messageId: message.id,
          senderType: message.sender_type,
          existsById,
          existsByWhatsAppId,
          existsByDatabaseId,
          existsByContentAndTime,
          databaseMessageId
        });
        return prev;
      }
      
      console.log('✅ useMessageData: Adding new message to state', {
        messageId: message.id,
        senderType: message.sender_type,
        currentCount: prev.length,
        newCount: prev.length + 1,
        isAIMessage
      });
      
      // High-performance append: if message is newer than all existing, append directly
      // Otherwise fall back to sorting for proper chronological order
      const lastMessage = prev[prev.length - 1];
      const newMessages = !lastMessage || new Date(message.created_at) >= new Date(lastMessage.created_at)
        ? [...prev, message]
        : MessageDataService.sortMessagesByDate([...prev, message]);
      
      // Update hash for change detection - include media fields
      const newHash = JSON.stringify(newMessages.map(m => ({ 
        id: m.id, 
        created_at: m.created_at, 
        sender_type: m.sender_type,
        media_url: m.media_url,
        metadata: m.metadata,
        content: m.content 
      })));
      messageHashRef.current = newHash;
      
      // Invalidate cache - ONLY if feature enabled
      // Use refs for correct cache key during transitions (avoids stale closure)
      const currentCacheKey = customerIdRef.current ? `customer-${customerIdRef.current}` : conversationId;
      if (currentCacheKey && FEATURES.ENABLE_MESSAGE_CACHE) {
        messageCache.delete(currentCacheKey);
        console.log('🗑️ Cache invalidated due to new message', { cacheKey: currentCacheKey });
      }

      // New conversation detection (customer mode): message from unknown conversation
      if (customerIdRef.current && allConversationIdsRef.current) {
        const isNewConversation = !allConversationIdsRef.current.includes(message.conversation_id);
        if (isNewConversation) {
          if (import.meta.env.DEV) console.log('🆕 New conversation detected for customer', {
            conversationId: message.conversation_id,
            customerId: customerIdRef.current
          });
          // Cache already busted above — sidebar INSERT handler will update allConversationIds
        }
      }

      console.log('✅ useMessageData: Message state updated', {
        totalMessages: newMessages.length,
        aiMessages: newMessages.filter(m => m.sender_type === 'ai_agent_automatic').length,
        lastMessage: newMessages[newMessages.length - 1]?.id
      });

      return newMessages;
    });
  }, [conversationId]);

  const updateMessage = useCallback((messageId: string, updates: Partial<Message>) => {
    setMessages(prev => 
      prev.map(msg => 
        msg.id === messageId ? { ...msg, ...updates } : msg
      )
    );
  }, []);

  const updateMessageByWhatsAppId = useCallback((whatsappId: string, updates: Partial<Message>) => {
    setMessages(prev => 
      prev.map(msg => 
        msg.external_id === whatsappId ? { ...msg, ...updates } : msg
      )
    );
  }, []);

  return {
    messages,
    isLoading: localLoading,  // [FIX: CACHE-LOADING] Use local loading state we control
    error,
    loadMessages: loadMessagesWrapper,
    silentRefresh,
    addMessage,
    updateMessage,
    updateMessageByWhatsAppId,
    loadMoreMessages,
    hasMoreMessages,
    isLoadingMore
  };
}
